import '../i18n/autoTranslate';
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  Bookmark,
  BookmarkCheck,
  Brain,
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronRight,
  Code2,
  Copy,
  Filter,
  LibraryBig,
  Lightbulb,
  MessageSquareText,
  Play,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  X,
} from "lucide-react";

/*
  Put this file in the same folder as your JSON file.
  It automatically reads any file matching: interview_questions_db*.json
  Examples:
  - interview_questions_db.json
  - interview_questions_db (1).json
*/
const questionBankModules = import.meta.glob("./interview_questions_db*.json", { eager: true });
const questionsDb = Object.values(questionBankModules)?.[0]?.default || {
  common_questions: [],
  industries: {},
  roles: {},
};

const APP_NAME = "InterviewGenieAI";

/* -------------------------------------------------------------------------- */
/*                                UI Primitives                               */
/* -------------------------------------------------------------------------- */

const cn = (...classes) => classes.filter(Boolean).join(" ");

const AppBackground = ({ children }) => (
  <div className="min-h-screen w-full bg-[#07090c] text-white relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-emerald-400/10 blur-[80px]" />
      <div className="absolute -bottom-28 -right-24 h-[560px] w-[560px] rounded-full bg-sky-400/10 blur-[90px]" />
      <div className="absolute top-1/3 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-white/[0.035] blur-[90px]" />
      <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_65%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
      <div className="absolute inset-0 opacity-35 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:26px_26px]" />
      <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:92px_92px]" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-black/70" />
    </div>
    <div className="relative">{children}</div>
  </div>
);

const Glass = ({ children, className = "" }) => (
  <div
    className={cn(
      "rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl",
      "shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]",
      className
    )}
  >
    {children}
  </div>
);

const PrimaryButton = ({ children, onClick, disabled, className = "", type = "button" }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl font-semibold text-sm",
      "bg-white text-black hover:bg-white/90 active:scale-[0.99]",
      "shadow-[0_12px_30px_-18px_rgba(255,255,255,0.50)]",
      "transition disabled:opacity-50 disabled:hover:bg-white disabled:cursor-not-allowed",
      className
    )}
  >
    {children}
  </button>
);

const GhostButton = ({ children, onClick, disabled, className = "", type = "button" }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl font-semibold text-sm",
      "border border-white/12 bg-white/5 hover:bg-white/8 active:scale-[0.99]",
      "text-white/85 transition disabled:opacity-50 disabled:cursor-not-allowed",
      className
    )}
  >
    {children}
  </button>
);

const Pill = ({ children, tone = "neutral", className = "" }) => {
  const cls =
    tone === "emerald"
      ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-100"
      : tone === "sky"
      ? "border-sky-400/20 bg-sky-400/10 text-sky-100"
      : tone === "amber"
      ? "border-amber-300/25 bg-amber-300/10 text-amber-100"
      : tone === "rose"
      ? "border-rose-300/25 bg-rose-300/10 text-rose-100"
      : "border-white/12 bg-white/6 text-white/80";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide",
        cls,
        className
      )}
    >
      {children}
    </span>
  );
};

const TopBar = ({ onHome }) => (
  <div className="flex items-center justify-between px-5 sm:px-6 py-5 mt-20">
    <button onClick={onHome} className="group inline-flex items-center gap-2" title={APP_NAME}>
      <div className="h-9 w-9 rounded-2xl bg-white/8 border border-white/12 backdrop-blur-xl flex items-center justify-center">
        <Sparkles className="h-4.5 w-4.5 text-white/85" />
      </div>
      <div className="leading-tight text-left">
        <div className="text-sm font-semibold tracking-tight">Questions Bank</div>
        <div className="text-[11px] text-white/50 -mt-0.5">{APP_NAME}</div>
      </div>
    </button>

    <div className="hidden md:flex items-center gap-2">
      <Pill tone="emerald">Smart answers</Pill>
      <Pill tone="sky">Practice-ready</Pill>
      <Pill tone="amber">Role focused</Pill>
    </div>

    <div className="flex items-center gap-2">
      <GhostButton className="hidden sm:inline-flex px-4" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
        <ArrowUpRight className="h-4 w-4" />
        Top
      </GhostButton>
    </div>
  </div>
);

/* -------------------------------------------------------------------------- */
/*                             Data + answer engine                           */
/* -------------------------------------------------------------------------- */

const prettify = (value = "") =>
  String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const normalize = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[’`]/g, "'")
    .replace(/[^a-z0-9+/#.\s-]/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const stableId = (source, category, question, index) =>
  `${source}-${category || "general"}-${index}-${normalize(question).slice(0, 45).replace(/\s/g, "-")}`;

const flattenQuestionBank = (db) => {
  const rows = [];
  const seen = new Set();

  (db.common_questions || []).forEach((question, index) => {
    const key = normalize(question);
    if (seen.has(`common:${key}`)) return;
    seen.add(`common:${key}`);
    rows.push({
      id: stableId("common", "general", question, index),
      source: "common",
      category: "general",
      label: "Common",
      question,
      index,
    });
  });

  Object.entries(db.industries || {}).forEach(([industry, questions]) => {
    (questions || []).forEach((question, index) => {
      const key = normalize(question);
      if (seen.has(`industry:${industry}:${key}`)) return;
      seen.add(`industry:${industry}:${key}`);
      rows.push({
        id: stableId("industry", industry, question, index),
        source: "industry",
        category: industry,
        label: prettify(industry),
        question,
        index,
      });
    });
  });

  Object.entries(db.roles || {}).forEach(([role, questions]) => {
    (questions || []).forEach((question, index) => {
      const key = normalize(question);
      if (seen.has(`role:${role}:${key}`)) return;
      seen.add(`role:${role}:${key}`);
      rows.push({
        id: stableId("role", role, question, index),
        source: "role",
        category: role,
        label: prettify(role),
        question,
        index,
      });
    });
  });

  return rows;
};

const getQuestionIntent = (question = "") => {
  const q = normalize(question);
  if (q.startsWith("tell me about a time") || q.includes("describe a time") || q.includes("time you")) return "behavioral";
  if (q.startsWith("explain") || q.includes("difference between") || q.includes("concept of")) return "technical";
  if (q.includes("approach") || q.includes("strategy") || q.includes("plan")) return "strategic";
  if (q.includes("experience with")) return "experience";
  if (q.includes("handle") || q.includes("manage")) return "situational";
  return "general";
};

const getDifficulty = (question = "") => {
  const q = normalize(question);
  const advanced = [
    "distributed",
    "eventual consistency",
    "system design",
    "architecture",
    "valuation",
    "regulatory",
    "compliance",
    "sharding",
    "consensus",
    "observability",
    "risk assessment",
    "transformation",
    "strategy",
  ];
  const intermediate = ["explain", "approach", "optimize", "analyze", "design", "prioritize", "roadmap"];
  if (advanced.some((token) => q.includes(token))) return "advanced";
  if (intermediate.some((token) => q.includes(token))) return "intermediate";
  return "foundational";
};

const technicalConceptAnswers = [
  {
    match: ["tcp and udp"],
    answer:
      "TCP is connection-oriented, reliable, and ordered, so it is best when accuracy matters, like APIs, file transfer, and database communication. UDP is connectionless and faster because it does not guarantee delivery or ordering, so it fits real-time use cases like gaming, streaming, and voice. In an interview, I would mention the trade-off clearly: TCP optimizes reliability; UDP optimizes speed and low latency.",
    bullets: ["Define both protocols", "Compare reliability vs latency", "Give real use cases", "Close with the trade-off"],
  },
  {
    match: ["rest vs graphql", "rest and graphql"],
    answer:
      "REST exposes resources through multiple endpoints and is simple, cache-friendly, and widely adopted. GraphQL exposes a typed query layer where clients request exactly the fields they need, which can reduce over-fetching and improve frontend flexibility. I would choose REST for straightforward resource APIs and GraphQL when clients need flexible data shapes across web, mobile, or complex dashboards.",
    bullets: ["Compare API shape", "Mention over-fetching", "Explain when each is better", "Show product judgment"],
  },
  {
    match: ["sql and nosql", "sql vs nosql", "between sql and nosql"],
    answer:
      "SQL databases are relational, schema-driven, and strong for structured data, transactions, and complex joins. NoSQL databases are more flexible and often better for high-volume, distributed, or document/key-value workloads. My decision depends on data shape, consistency needs, query patterns, and scale. For example, payments usually fit SQL; event logs or flexible user profiles may fit NoSQL.",
    bullets: ["Data model", "Consistency", "Query patterns", "Example use cases"],
  },
  {
    match: ["cap theorem"],
    answer:
      "CAP theorem says that in a distributed system, during a network partition, you must choose between consistency and availability. Consistency means every read gets the latest write; availability means every request gets a response. The practical point is not choosing all three equally, but designing the right trade-off for the product: banking leans consistency, while social feeds may accept temporary inconsistency for availability.",
    bullets: ["Define C, A, P", "Explain partition reality", "Give product examples", "Mention trade-off"],
  },
  {
    match: ["microservices"],
    answer:
      "Microservices split a system into independently deployable services aligned around business capabilities. The benefits are team autonomy, scalability, and isolated deployments. The trade-offs are operational complexity, distributed debugging, network failures, and data consistency. I would use microservices when team and domain complexity justify them, not just because they sound modern.",
    bullets: ["Define", "Benefits", "Trade-offs", "When not to use"],
  },
  {
    match: ["ci/cd", "continuous integration", "continuous delivery"],
    answer:
      "CI/CD is the practice of continuously integrating code, validating it with automated tests, and safely delivering or deploying it. A strong pipeline includes linting, unit tests, integration tests, security checks, build artifacts, staged deployments, and rollback strategy. The goal is faster releases with lower risk, not just automation for its own sake.",
    bullets: ["Pipeline stages", "Quality gates", "Deployment safety", "Business value"],
  },
  {
    match: ["database indexing", "indexing"],
    answer:
      "Database indexes speed up reads by creating a data structure that helps the database find rows without scanning the full table. I use indexes on columns that appear often in WHERE, JOIN, ORDER BY, or foreign-key lookups. The trade-off is that indexes consume storage and slow down writes, so I validate them with query plans and real usage patterns.",
    bullets: ["Purpose", "Where to use", "Trade-offs", "Measure with query plans"],
  },
  {
    match: ["horizontal and vertical scaling"],
    answer:
      "Vertical scaling means making one machine stronger with more CPU, RAM, or storage. Horizontal scaling means adding more machines or instances behind a load balancer. Vertical scaling is simpler but limited; horizontal scaling is more resilient and scalable, but requires stateless services, good load balancing, and careful data consistency design.",
    bullets: ["Define both", "Pros and limits", "Architecture requirements", "Example"],
  },
  {
    match: ["authentication and authorization"],
    answer:
      "Authentication verifies who the user is; authorization determines what that user is allowed to do. For example, logging in with email and password is authentication, while checking whether the user can access an admin dashboard is authorization. A secure design separates both, uses least privilege, protects tokens, and validates permissions server-side.",
    bullets: ["Who you are", "What you can do", "Example", "Security practice"],
  },
  {
    match: ["caching"],
    answer:
      "Caching stores frequently used data closer to where it is needed to reduce latency and load. I think about what to cache, where to cache it, how long it should live, and how to invalidate it safely. The biggest risk is stale data, so I use TTLs, cache keys, invalidation rules, and monitoring to make sure performance improves without damaging correctness.",
    bullets: ["Why cache", "Where cache lives", "Invalidation", "Staleness risk"],
  },
  {
    match: ["message queues", "message-driven", "event-driven"],
    answer:
      "Message queues decouple producers from consumers so work can happen asynchronously and reliably. They are useful for background jobs, notifications, payments, analytics, and integration between services. A strong implementation handles retries, dead-letter queues, idempotency, monitoring, and back-pressure so failures do not silently lose work.",
    bullets: ["Decoupling", "Async workloads", "Reliability patterns", "Failure handling"],
  },
  {
    match: ["idempotency", "idempotent"],
    answer:
      "Idempotency means running the same operation multiple times produces the same final result. It is important for payments, retries, webhooks, and distributed systems because networks fail and clients may retry. I usually implement it with idempotency keys, unique constraints, safe state transitions, and clear API semantics.",
    bullets: ["Define", "Why retries matter", "Implementation", "Examples"],
  },
  {
    match: ["acid"],
    answer:
      "ACID stands for Atomicity, Consistency, Isolation, and Durability. It describes transaction guarantees that keep database operations safe and reliable. Atomicity means all-or-nothing, consistency means valid state, isolation prevents transactions interfering unexpectedly, and durability means committed data survives failures.",
    bullets: ["Name each letter", "Define simply", "Tie to transactions", "Mention reliability"],
  },
  {
    match: ["load balancing"],
    answer:
      "Load balancing distributes traffic across multiple servers so the system can handle more users and avoid a single point of failure. A good setup considers health checks, routing strategy, sticky sessions only when needed, autoscaling, and graceful shutdowns. The goal is reliability, performance, and smoother scaling.",
    bullets: ["Traffic distribution", "Health checks", "Scaling", "Reliability"],
  },
  {
    match: ["serverless"],
    answer:
      "Serverless lets teams run code without managing servers directly. It can reduce operational overhead and scale automatically for event-driven workloads. The trade-offs include cold starts, vendor lock-in, execution limits, and debugging complexity. I would use it for async jobs, APIs with variable traffic, scheduled tasks, or lightweight integrations.",
    bullets: ["Definition", "Benefits", "Trade-offs", "Best use cases"],
  },
  {
    match: ["cloud computing"],
    answer:
      "Cloud computing provides on-demand infrastructure and managed services such as compute, storage, databases, networking, and AI services. The value is speed, scalability, resilience, and reduced infrastructure burden. The professional answer is to mention not only services used, but how they improved reliability, cost, deployment speed, or product delivery.",
    bullets: ["Services", "Business value", "Reliability", "Cost awareness"],
  },
];

const exactCommonAnswers = {
  "tell me about yourself.": {
    answer:
      "I’m a [role] with experience in [main skill/industry]. Most of my work has focused on [core responsibility], where I’ve delivered [measurable result]. What makes me a strong fit for this role is the combination of [skill 1], [skill 2], and the ability to work with teams to turn goals into outcomes. I’m now looking for a role where I can contribute to [company/team goal] and keep growing in [area].",
    bullets: ["Current role", "Relevant experience", "Measurable impact", "Why this role now"],
  },
  "why do you want to work here?": {
    answer:
      "I’m interested in this company because [specific company reason: product, mission, market, team, or technology] aligns with the kind of work I want to do. I noticed [specific detail from research], and that tells me the team values [value]. My background in [skill/experience] would help me contribute quickly, especially around [specific area].",
    bullets: ["Show research", "Connect to values", "Connect your skills", "Name contribution area"],
  },
  "what are your greatest strengths?": {
    answer:
      "One of my strongest strengths is [strength], especially in situations where [context]. For example, in [project/situation], I used that strength to [action], which resulted in [measurable impact]. I think this strength matters for this role because [connect to job requirement].",
    bullets: ["Name one strength", "Prove it", "Quantify it", "Connect it to the job"],
  },
  "what are your weaknesses?": {
    answer:
      "One area I’ve been improving is [real but non-critical weakness]. Earlier, it affected me when [brief context]. I’ve been addressing it by [specific action/system], and I’ve already seen progress through [result]. I’m still improving it, but I’m much more intentional about it now.",
    bullets: ["Real weakness", "Low-risk", "Action taken", "Evidence of progress"],
  },
  "where do you see yourself in 5 years?": {
    answer:
      "In five years, I want to be operating at a higher level of ownership in [role/function]. I want to deepen my skills in [area], lead larger initiatives, and become someone the team trusts for both execution and judgment. This role is a strong step because it gives me the chance to contribute now while growing toward that longer-term direction.",
    bullets: ["Growth direction", "Skill depth", "Team contribution", "Fit with current role"],
  },
  "why are you leaving your current job?": {
    answer:
      "I’ve learned a lot in my current role, especially around [positive learning]. At this stage, I’m looking for a new opportunity with more alignment around [growth area, challenge, industry, or responsibility]. I’m not leaving because of negativity; I’m moving toward a role where I can contribute more directly to [target outcome].",
    bullets: ["Stay positive", "Show growth", "Avoid blame", "Move toward opportunity"],
  },
  "what is your greatest professional achievement?": {
    answer:
      "My greatest professional achievement was [achievement]. The situation was [context], and the challenge was [problem]. I took ownership of [actions], worked with [stakeholders], and delivered [measurable result]. What I’m proud of is not only the result, but the way it improved [team/customer/business impact].",
    bullets: ["Use STAR", "Show ownership", "Quantify impact", "Explain why it mattered"],
  },
  "describe a challenge you faced and how you overcame it.": {
    answer:
      "A challenge I faced was [challenge]. The main difficulty was [constraint]. I broke the problem down, aligned with [stakeholders/team], and focused on [specific action]. The result was [outcome], and the lesson I took from it was [learning]. Since then, I apply that lesson by [new behavior].",
    bullets: ["Challenge", "Constraint", "Action", "Result", "Lesson"],
  },
  "how do you handle stress and pressure?": {
    answer:
      "I handle pressure by making the situation clear, separating urgent from important, and focusing on the next highest-impact action. When pressure increases, I communicate early, protect quality, and break the work into smaller milestones. For example, [brief example], I stayed focused by [action], and we achieved [result].",
    bullets: ["Calm system", "Prioritization", "Communication", "Example"],
  },
  "what motivates you?": {
    answer:
      "I’m motivated by solving meaningful problems and seeing my work create measurable value. I enjoy roles where I can learn quickly, collaborate with strong people, and turn complexity into something useful. In this role, I would be especially motivated by [specific company/team mission].",
    bullets: ["Intrinsic motivation", "Value creation", "Learning", "Company connection"],
  },
  "how do you prioritize your work?": {
    answer:
      "I prioritize by clarifying impact, urgency, dependencies, and risk. I first make sure I understand the goal, then I identify what will create the most value or unblock others. I communicate trade-offs early, and when priorities change, I realign quickly instead of trying to do everything at the same time.",
    bullets: ["Impact", "Urgency", "Dependencies", "Trade-offs"],
  },
  "tell me about a time you failed.": {
    answer:
      "A time I failed was [situation]. I misjudged [factor], which led to [consequence]. I took responsibility by [action], communicated with [stakeholders], and corrected it through [solution]. The lesson was [learning], and since then I’ve changed my process by [preventive action].",
    bullets: ["Own it", "No excuses", "Recovery", "Learning system"],
  },
  "how do you handle criticism?": {
    answer:
      "I try to treat criticism as information, not as a personal attack. First, I listen and make sure I understand the specific behavior or result that needs improvement. Then I decide what action to take and follow up when appropriate. Good feedback has helped me improve [example skill/process].",
    bullets: ["Listen", "Clarify", "Act", "Show maturity"],
  },
  "what's your work style?": {
    answer:
      "My work style is structured, collaborative, and outcome-focused. I like to clarify expectations early, break work into clear milestones, and communicate progress before there are surprises. I can work independently, but I also know when alignment with the team is important to move faster.",
    bullets: ["Structure", "Communication", "Ownership", "Collaboration"],
  },
  "how do you handle conflict with coworkers?": {
    answer:
      "I handle conflict by focusing on the shared goal, not personalities. I first try to understand the other person’s perspective, then clarify where we disagree and what decision needs to be made. If needed, I bring data, options, and trade-offs so the conversation becomes objective and productive.",
    bullets: ["Shared goal", "Listen", "Clarify disagreement", "Use data"],
  },
  "what are your salary expectations?": {
    answer:
      "I’m flexible and open to discussing a fair range based on the responsibilities, total compensation, and growth opportunity. Based on my research and experience, I’m targeting around [range], but I’m more focused on finding the right fit where I can create strong value.",
    bullets: ["Give range", "Mention total compensation", "Stay flexible", "Show value focus"],
  },
  "why should we hire you?": {
    answer:
      "You should hire me because I bring [skill 1], [skill 2], and a track record of [result]. I understand the role requires [requirement], and I’ve already demonstrated that through [example]. I’m confident I can contribute quickly, learn what’s specific to your environment, and help the team achieve [outcome].",
    bullets: ["Match requirements", "Proof", "Confidence", "Team outcome"],
  },
  "do you prefer working independently or in a team?": {
    answer:
      "I’m comfortable with both. I like working independently when ownership and focus are needed, but I also value teamwork because strong collaboration prevents blind spots and creates better results. The key for me is clarity: knowing when to execute independently and when to align with others.",
    bullets: ["Both", "When independent", "When team", "Judgment"],
  },
  "what makes you unique?": {
    answer:
      "What makes me unique is the combination of [skill/trait 1], [skill/trait 2], and [experience]. I’m not only able to [technical/functional ability], but I also communicate clearly and stay focused on business impact. That combination helps me turn ideas into practical results.",
    bullets: ["Combination", "Proof", "Business impact", "Memorable positioning"],
  },
  "tell me about a time you showed leadership.": {
    answer:
      "I showed leadership when [situation]. The team needed [goal], but we faced [challenge]. I stepped up by [action], aligned people around [plan], and helped the team deliver [result]. What made it leadership was not the title, but taking responsibility and helping others move forward.",
    bullets: ["No title required", "Situation", "Influence", "Result"],
  },
  "what questions do you have for us?": {
    answer:
      "I’d love to understand three things: first, what success looks like in the first 90 days; second, what the biggest challenge is for this team right now; and third, how the company supports growth and feedback. Those answers would help me understand where I can create value fastest.",
    bullets: ["90-day success", "Team challenge", "Growth culture", "Value creation"],
  },
};

const extractTopic = (question = "") => {
  const clean = question.replace(/[?.]/g, "").trim();
  const patterns = [
    /what(?:'s| is) your experience with (.+)$/i,
    /how do you approach (.+)$/i,
    /how do you handle (.+)$/i,
    /explain the concept of (.+)$/i,
    /explain (.+)$/i,
    /describe (.+)$/i,
  ];

  for (const pattern of patterns) {
    const match = clean.match(pattern);
    if (match?.[1]) return match[1].trim();
  }

  return clean;
};

const buildBestResponse = (row) => {
  const question = row?.question || "";
  const q = normalize(question);
  const intent = getQuestionIntent(question);
  const topic = extractTopic(question);

  const exact = exactCommonAnswers[q];
  if (exact) {
    return {
      answer: exact.answer,
      formula: "Answer in 45–75 seconds. Keep it specific, positive, and connected to the role.",
      structure: exact.bullets,
      whyItWorks: [
        "It gives the interviewer a clean story instead of random details.",
        "It balances confidence with humility.",
        "It connects your background to the company’s needs.",
      ],
      mistakes: ["Giving a life story", "Being vague", "Forgetting measurable impact"],
      powerWords: ["delivered", "improved", "owned", "aligned", "measured"],
    };
  }

  const tech = technicalConceptAnswers.find((item) => item.match.some((token) => q.includes(token)));
  if (tech) {
    return {
      answer: tech.answer,
      formula: "Define → compare/trade-off → use case → practical judgment.",
      structure: tech.bullets,
      whyItWorks: [
        "It proves you understand the concept, not just memorized a definition.",
        "It shows trade-off thinking, which senior interviewers love.",
        "It turns a technical answer into product and architecture judgment.",
      ],
      mistakes: ["Only giving a definition", "Ignoring trade-offs", "No real-world example"],
      powerWords: ["trade-off", "latency", "reliability", "scalability", "constraints"],
    };
  }

  if (q.includes("experience with")) {
    return {
      answer: `My experience with ${topic} is strongest in situations where it has to solve a real business or technical problem, not just exist as a tool. I would explain the context I used it in, the decisions I made, the trade-offs I considered, and the measurable result. For example: “I used ${topic} to [solve problem], by [specific action], which improved [metric/result].” I would also be honest about what I’m still learning and how I would ramp up quickly.`,
      formula: "Context → responsibility → action → result → learning edge.",
      structure: ["Where you used it", "What you personally owned", "Result or metric", "What you learned"],
      whyItWorks: [
        "It avoids fake expertise and creates trust.",
        "It shows ownership instead of just exposure.",
        "It gives the interviewer proof through outcomes.",
      ],
      mistakes: ["Saying only ‘I have experience’", "Listing tools without impact", "Overclaiming"],
      powerWords: ["implemented", "optimized", "measured", "scaled", "validated"],
    };
  }

  if (q.startsWith("how do you approach")) {
    return {
      answer: `My approach to ${topic} starts with clarifying the goal, constraints, success metrics, and stakeholders. Then I break the problem into smaller parts, identify the highest-risk assumptions, and choose the simplest solution that can create measurable progress. I communicate trade-offs early, test the approach with data or feedback, and iterate until the result is reliable.`,
      formula: "Goal → constraints → options → trade-offs → execution → measurement.",
      structure: ["Clarify the goal", "Identify constraints", "Choose approach", "Measure outcome"],
      whyItWorks: [
        "It makes you sound structured and calm.",
        "It proves you think before executing.",
        "It shows maturity through trade-off and measurement language.",
      ],
      mistakes: ["Jumping straight to tactics", "No success metric", "No stakeholder awareness"],
      powerWords: ["clarify", "prioritize", "validate", "iterate", "align"],
    };
  }

  if (q.startsWith("how do you handle")) {
    return {
      answer: `I handle ${topic} by first understanding the facts, the impact, and the people involved. I stay calm, separate emotion from the decision, and focus on the shared objective. Then I choose the next practical step, communicate clearly, and follow up to make sure the issue is actually resolved. A strong example would be: [situation], [action], [result], [lesson].`,
      formula: "Stay calm → understand facts → align on goal → act → follow up.",
      structure: ["Facts", "Impact", "Communication", "Resolution", "Lesson"],
      whyItWorks: [
        "It signals emotional control.",
        "It shows you solve problems without drama.",
        "It gives a ready STAR structure for proof.",
      ],
      mistakes: ["Blaming people", "Sounding reactive", "No follow-up"],
      powerWords: ["resolved", "aligned", "de-escalated", "communicated", "owned"],
    };
  }

  if (q.startsWith("explain the difference") || q.includes("difference between")) {
    return {
      answer: `The best way to answer is to compare the two concepts through purpose, behavior, trade-offs, and use cases. I would say: “The main difference is that one is optimized for [dimension], while the other is optimized for [dimension]. In practice, I would choose the first when [use case] and the second when [use case]. The decision depends on constraints like performance, reliability, complexity, cost, and user impact.”`,
      formula: "Define both → compare dimensions → use cases → decision rule.",
      structure: ["Definition A", "Definition B", "Trade-off", "When to use each"],
      whyItWorks: [
        "It turns memorization into judgment.",
        "It gives the interviewer a clear decision framework.",
        "It works even for very technical comparison questions.",
      ],
      mistakes: ["Only defining one side", "No trade-off", "No use case"],
      powerWords: ["trade-off", "optimized", "constraints", "use case", "decision"],
    };
  }

  if (q.startsWith("explain")) {
    return {
      answer: `${topic} is best explained by first defining it simply, then showing why it matters in a real system or business context. I would say what problem it solves, where it is commonly used, the main benefits, and the trade-offs or risks. A strong close is: “I would apply it when [condition], but I’d be careful about [risk].”`,
      formula: "Simple definition → problem solved → example → trade-off → when to use.",
      structure: ["Definition", "Purpose", "Example", "Trade-off", "Practical decision"],
      whyItWorks: [
        "It makes complex topics easy to understand.",
        "It shows practical application, not just theory.",
        "It gives senior-level judgment through trade-offs.",
      ],
      mistakes: ["Too much theory", "No example", "Ignoring limitations"],
      powerWords: ["solves", "enables", "trade-off", "reliable", "scalable"],
    };
  }

  if (intent === "behavioral" || q.includes("describe a time") || q.includes("tell me about a time")) {
    return {
      answer:
        "I would answer this using STAR. Situation: briefly explain the context. Task: describe what was expected from you. Action: focus on what you personally did, including decisions and communication. Result: finish with a measurable outcome and a lesson. A strong answer sounds like: ‘In [situation], I was responsible for [task]. I did [specific actions], and the result was [metric/outcome]. The lesson I took was [learning].’",
      formula: "STAR: Situation → Task → Action → Result + Lesson.",
      structure: ["Situation", "Task", "Action", "Result", "Lesson"],
      whyItWorks: [
        "It prevents rambling.",
        "It shows ownership and maturity.",
        "It ends with proof and self-awareness.",
      ],
      mistakes: ["No result", "Too much background", "Saying ‘we’ without your own action"],
      powerWords: ["owned", "led", "improved", "reduced", "delivered"],
    };
  }

  return {
    answer:
      "A strong answer should be specific, structured, and connected to the role. Start with a direct one-sentence answer, support it with a concrete example, explain your action, and close with the result or lesson. Avoid generic claims. A strong pattern is: ‘My answer is [direct point]. For example, [situation]. I handled it by [action]. The result was [impact], and that matters for this role because [connection].’",
    formula: "Direct answer → proof → impact → role connection.",
    structure: ["Direct point", "Concrete example", "Personal action", "Result", "Role connection"],
    whyItWorks: [
      "It respects the interviewer’s attention.",
      "It turns vague answers into proof.",
      "It creates a confident closing signal.",
    ],
    mistakes: ["Vague answers", "No example", "No connection to the job"],
    powerWords: ["specific", "measured", "collaborated", "delivered", "improved"],
  };
};

const sourceTone = {
  common: "emerald",
  industry: "sky",
  role: "amber",
};

const intentTone = {
  behavioral: "emerald",
  technical: "sky",
  strategic: "amber",
  experience: "neutral",
  situational: "rose",
  general: "neutral",
};

const sourceIcon = {
  common: MessageSquareText,
  industry: Building2,
  role: Briefcase,
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export default function QuestionsBank() {
  const navigate = useNavigate();
  const allQuestions = useMemo(() => flattenQuestionBank(questionsDb), []);
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("all");
  const [category, setCategory] = useState("all");
  const [intent, setIntent] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [selectedId, setSelectedId] = useState(allQuestions[0]?.id || null);
  const [copied, setCopied] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("questions_bank_favorites") || "[]");
    } catch {
      return [];
    }
  });
  const [focusMode, setFocusMode] = useState(false);

  const categories = useMemo(() => {
    const filtered = source === "all" ? allQuestions : allQuestions.filter((q) => q.source === source);
    const unique = [...new Set(filtered.map((q) => q.category))].filter(Boolean);
    return unique.sort((a, b) => prettify(a).localeCompare(prettify(b)));
  }, [allQuestions, source]);

  const enrichedQuestions = useMemo(
    () =>
      allQuestions.map((item) => ({
        ...item,
        intent: getQuestionIntent(item.question),
        difficulty: getDifficulty(item.question),
        answer: buildBestResponse(item),
      })),
    [allQuestions]
  );

  const filteredQuestions = useMemo(() => {
    const needle = normalize(query);
    return enrichedQuestions.filter((item) => {
      const matchesSearch =
        !needle ||
        normalize(item.question).includes(needle) ||
        normalize(item.label).includes(needle) ||
        normalize(item.answer.answer).includes(needle);
      const matchesSource = source === "all" || item.source === source;
      const matchesCategory = category === "all" || item.category === category;
      const matchesIntent = intent === "all" || item.intent === intent;
      const matchesDifficulty = difficulty === "all" || item.difficulty === difficulty;
      return matchesSearch && matchesSource && matchesCategory && matchesIntent && matchesDifficulty;
    });
  }, [enrichedQuestions, query, source, category, intent, difficulty]);

  const selected = useMemo(
    () => filteredQuestions.find((q) => q.id === selectedId) || filteredQuestions[0] || enrichedQuestions[0],
    [filteredQuestions, selectedId, enrichedQuestions]
  );

  const stats = useMemo(() => {
    const common = allQuestions.filter((q) => q.source === "common").length;
    const industries = allQuestions.filter((q) => q.source === "industry").length;
    const roles = allQuestions.filter((q) => q.source === "role").length;
    return { total: allQuestions.length, common, industries, roles };
  }, [allQuestions]);

  useEffect(() => {
    if (!filteredQuestions.some((q) => q.id === selectedId)) {
      setSelectedId(filteredQuestions[0]?.id || null);
    }
  }, [filteredQuestions, selectedId]);

  useEffect(() => {
    localStorage.setItem("questions_bank_favorites", JSON.stringify(favorites));
  }, [favorites]);

  const resetFilters = () => {
    setQuery("");
    setSource("all");
    setCategory("all");
    setIntent("all");
    setDifficulty("all");
  };

  const toggleFavorite = (id) => {
    setFavorites((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const copyAnswer = async () => {
    if (!selected) return;
    const answerText = `Question: ${selected.question}\n\nBest response:\n${selected.answer.answer}\n\nStructure:\n${selected.answer.structure.join(" → ")}\n\nFormula: ${selected.answer.formula}`;
    try {
      await navigator.clipboard.writeText(answerText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const startPractice = () => {
    if (!selected) return;

    const isTechnical = selected.intent === "technical" || selected.source === "role" || selected.category === "technology";
    const configuration = {
      selectedType: isTechnical ? "technical" : "behavioral",
      selectedIndustry: selected.source === "industry" ? selected.category : "",
      selectedRole: selected.source === "role" ? selected.category : "",
      selectedCompanyType: "",
      estimatedQuestions: 1,
      personality: {
        selectedPersona: { id: "questions-bank-coach", name: "Focused Coach" },
        tone: 4,
        difficulty: selected.difficulty === "advanced" ? 5 : selected.difficulty === "intermediate" ? 4 : 3,
        pace: 3,
        pressure: 3,
        accent: "american",
      },
      selectedCategories: [selected.intent === "technical" ? "technical" : "behavioral"],
      selectedCompanies: [],
      interviewType: isTechnical ? "technical" : "behavioral",
      customQuestions: [selected.question],
      enableFollowUps: true,
      surpriseQuestions: false,
      starMethodEnforcement: selected.intent === "behavioral",
      followUpIntensity: 3,
      adaptiveDifficulty: false,
      selectedFormat: "chat",
      duration: 10,
      elevenlabsVoice: null,
    };

    localStorage.setItem("finalInterviewConfiguration", JSON.stringify(configuration));
    localStorage.setItem(
      "questionsBankSelectedQuestion",
      JSON.stringify({
        id: selected.id,
        question: selected.question,
        bestResponse: selected.answer.answer,
        source: selected.source,
        category: selected.category,
        intent: selected.intent,
        difficulty: selected.difficulty,
      })
    );

    navigate("/chat-interview", {
      state: {
        finalInterviewConfiguration: configuration,
        fromQuestionsBank: true,
        selectedQuestion: selected.question,
      },
    });
  };

  if (!allQuestions.length) {
    return (
      <AppBackground>
        <TopBar onHome={() => navigate("/dashboard-home")} />
        <div className="mx-auto max-w-4xl px-6 py-12">
          <Glass className="p-8 text-center">
            <LibraryBig className="mx-auto h-10 w-10 text-white/70" />
            <h1 className="mt-4 text-2xl font-semibold">Question JSON not found</h1>
            <p className="mt-2 text-white/60">
              Put this page in the same folder as your JSON file. The file should match
              <span className="mx-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 font-mono text-xs">interview_questions_db*.json</span>.
            </p>
          </Glass>
        </div>
      </AppBackground>
    );
  }

  const SelectedSourceIcon = selected ? sourceIcon[selected.source] || BookOpen : BookOpen;
  const isFavorite = selected ? favorites.includes(selected.id) : false;

  return (
    <AppBackground>
      <TopBar onHome={() => navigate("/dashboard-home")} />

      <div className="mx-auto max-w-[1480px] px-5 sm:px-6 pb-12 pt-4">
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <aside className={cn("lg:col-span-3 space-y-5", focusMode && "hidden lg:block")}>
            <Glass className="p-5">
              <Pill tone="emerald">
                <Sparkles className="h-3.5 w-3.5" />
                Smart preparation library
              </Pill>
              <h1 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
                Master every interview question.
              </h1>
              <p className="mt-3 text-sm leading-6 text-white/60">
                Search by role, industry, or intent. Each question includes a senior-level answer structure that helps users feel clear, prepared, and confident.
              </p>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <StatCard label="Total" value={stats.total} icon={LibraryBig} />
                <StatCard label="Common" value={stats.common} icon={MessageSquareText} />
                <StatCard label="Industry" value={stats.industries} icon={Building2} />
                <StatCard label="Role" value={stats.roles} icon={Briefcase} />
              </div>
            </Glass>

            <Glass className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-semibold">Filters</p>
                  <p className="text-xs text-white/45">Reduce noise. Increase focus.</p>
                </div>
                <SlidersHorizontal className="h-4 w-4 text-white/45" />
              </div>

              <div className="space-y-3">
                <FilterGroup
                  label="Question source"
                  value={source}
                  onChange={(value) => {
                    setSource(value);
                    setCategory("all");
                  }}
                  options={[
                    { value: "all", label: "All" },
                    { value: "common", label: "Common" },
                    { value: "industry", label: "Industries" },
                    { value: "role", label: "Roles" },
                  ]}
                />

                <FilterGroup
                  label="Category"
                  value={category}
                  onChange={setCategory}
                  options={[{ value: "all", label: "All categories" }, ...categories.map((c) => ({ value: c, label: prettify(c) }))]}
                />

                <FilterGroup
                  label="Intent"
                  value={intent}
                  onChange={setIntent}
                  options={[
                    { value: "all", label: "All intents" },
                    { value: "behavioral", label: "Behavioral" },
                    { value: "technical", label: "Technical" },
                    { value: "strategic", label: "Strategic" },
                    { value: "experience", label: "Experience" },
                    { value: "situational", label: "Situational" },
                    { value: "general", label: "General" },
                  ]}
                />

                <FilterGroup
                  label="Difficulty"
                  value={difficulty}
                  onChange={setDifficulty}
                  options={[
                    { value: "all", label: "All levels" },
                    { value: "foundational", label: "Foundational" },
                    { value: "intermediate", label: "Intermediate" },
                    { value: "advanced", label: "Advanced" },
                  ]}
                />

                <GhostButton onClick={resetFilters} className="w-full">
                  <X className="h-4 w-4" />
                  Clear filters
                </GhostButton>
              </div>
            </Glass>
          </aside>

          <main className={cn("lg:col-span-5 space-y-5", focusMode && "lg:col-span-5")}>
            <Glass className="p-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/45" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search questions, roles, industries, or answer keywords..."
                  className="w-full rounded-2xl border border-white/10 bg-white/[0.055] py-3 pl-11 pr-4 text-sm text-white placeholder:text-white/35 outline-none transition focus:border-emerald-300/35 focus:bg-white/[0.075]"
                />
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="emerald">{filteredQuestions.length} shown</Pill>
                  <Pill tone="sky">{favorites.length} saved</Pill>
                  {query && <Pill>Search: {query}</Pill>}
                </div>

                <GhostButton className="px-3 py-2" onClick={() => setFocusMode((v) => !v)}>
                  <Filter className="h-4 w-4" />
                  {focusMode ? "Full layout" : "Focus mode"}
                </GhostButton>
              </div>
            </Glass>

            <Glass className="overflow-hidden">
              <div className="border-b border-white/10 px-5 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">Question library</p>
                  <p className="text-xs text-white/45">Pick one question. The answer system updates instantly.</p>
                </div>
                <BookOpen className="h-4 w-4 text-white/45" />
              </div>

              <div className="max-h-[720px] overflow-y-auto p-3 custom-scrollbar">
                {filteredQuestions.length === 0 ? (
                  <div className="p-10 text-center">
                    <Search className="mx-auto h-8 w-8 text-white/30" />
                    <p className="mt-4 text-sm font-semibold">No questions found</p>
                    <p className="mt-1 text-xs text-white/45">Try removing a filter or using fewer keywords.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredQuestions.map((item, idx) => {
                      const Icon = sourceIcon[item.source] || BookOpen;
                      const active = selected?.id === item.id;
                      const saved = favorites.includes(item.id);
                      return (
                        <button
                          key={item.id}
                          onClick={() => setSelectedId(item.id)}
                          className={cn(
                            "group w-full rounded-3xl border p-4 text-left transition",
                            active
                              ? "border-white/18 bg-white/[0.085] shadow-[0_18px_45px_-30px_rgba(255,255,255,0.5)]"
                              : "border-white/8 bg-white/[0.025] hover:border-white/14 hover:bg-white/[0.055]"
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={cn(
                                "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border",
                                active ? "border-emerald-300/25 bg-emerald-300/10" : "border-white/10 bg-white/5"
                              )}
                            >
                              <Icon className="h-4.5 w-4.5 text-white/75" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <Pill tone={sourceTone[item.source]}>{item.label}</Pill>
                                <Pill tone={intentTone[item.intent]}>{prettify(item.intent)}</Pill>
                                {saved && (
                                  <Pill tone="amber">
                                    <Star className="h-3 w-3" /> Saved
                                  </Pill>
                                )}
                              </div>
                              <p className="mt-3 text-sm sm:text-[15px] leading-6 text-white/88">{item.question}</p>
                              <div className="mt-3 flex items-center justify-between gap-3">
                                <p className="text-xs text-white/40">#{idx + 1} • {prettify(item.difficulty)}</p>
                                <ChevronRight className={cn("h-4 w-4 text-white/35 transition", active && "translate-x-0.5 text-white/70")} />
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </Glass>
          </main>

          <section className={cn("lg:col-span-4 space-y-5", focusMode && "lg:col-span-7")}>
            {selected && (
              <>
                <Glass className="p-5 sm:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/6">
                        <SelectedSourceIcon className="h-5 w-5 text-white/80" />
                      </div>
                      <div>
                        <div className="flex flex-wrap gap-2">
                          <Pill tone={sourceTone[selected.source]}>{selected.label}</Pill>
                          <Pill tone={intentTone[selected.intent]}>{prettify(selected.intent)}</Pill>
                          <Pill>{prettify(selected.difficulty)}</Pill>
                        </div>
                        <h2 className="mt-4 text-2xl font-semibold tracking-tight leading-tight">{selected.question}</h2>
                      </div>
                    </div>

                    <button
                      onClick={() => toggleFavorite(selected.id)}
                      className="rounded-2xl border border-white/10 bg-white/5 p-2.5 text-white/70 transition hover:bg-white/8 hover:text-white"
                      title={isFavorite ? "Remove from saved" : "Save question"}
                    >
                      {isFavorite ? <BookmarkCheck className="h-5 w-5" /> : <Bookmark className="h-5 w-5" />}
                    </button>
                  </div>

                  <div className="mt-6 rounded-3xl border border-white/10 bg-black/18 p-5">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <Brain className="h-4 w-4 text-emerald-200" />
                      Best response to personalize
                    </div>
                    <p className="mt-4 text-[15px] leading-7 text-white/78">{selected.answer.answer}</p>
                  </div>

                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <PrimaryButton onClick={startPractice}>
                      <Play className="h-4 w-4" />
                      Practice this question
                    </PrimaryButton>
                    <GhostButton onClick={copyAnswer}>
                      <Copy className="h-4 w-4" />
                      {copied ? "Copied" : "Copy answer"}
                    </GhostButton>
                  </div>
                </Glass>

                <Glass className="p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold">Answer architecture</p>
                      <p className="text-xs text-white/45">The psychological path: clarity first, proof second, confidence last.</p>
                    </div>
                    <Target className="h-4 w-4 text-white/45" />
                  </div>

                  <div className="mt-4 rounded-3xl border border-emerald-300/15 bg-emerald-300/[0.055] p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-emerald-100/65">Formula</p>
                    <p className="mt-2 text-sm leading-6 text-white/80">{selected.answer.formula}</p>
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selected.answer.structure.map((step, index) => (
                      <div key={`${step}-${index}`} className="rounded-2xl border border-white/10 bg-white/[0.035] p-4">
                        <div className="flex items-center gap-2">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs font-bold text-black">{index + 1}</span>
                          <p className="text-sm font-semibold">{step}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Glass>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Glass className="p-5">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <CheckCircle2 className="h-4 w-4 text-emerald-200" />
                      Why this works
                    </div>
                    <div className="mt-4 space-y-3">
                      {selected.answer.whyItWorks.map((item, index) => (
                        <InsightLine key={index} icon={TrendingUp} text={item} />
                      ))}
                    </div>
                  </Glass>

                  <Glass className="p-5">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <ShieldCheck className="h-4 w-4 text-sky-200" />
                      Avoid these mistakes
                    </div>
                    <div className="mt-4 space-y-3">
                      {selected.answer.mistakes.map((item, index) => (
                        <InsightLine key={index} icon={X} text={item} />
                      ))}
                    </div>
                  </Glass>
                </div>

                <Glass className="p-5">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Lightbulb className="h-4 w-4 text-amber-200" />
                    Power words
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {selected.answer.powerWords.map((word) => (
                      <Pill key={word} tone="amber">{word}</Pill>
                    ))}
                  </div>
                </Glass>
              </>
            )}
          </section>
        </section>
      </div>
    </AppBackground>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.035] p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-white/45">{label}</p>
        <Icon className="h-4 w-4 text-white/45" />
      </div>
      <p className="mt-3 text-2xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

function FilterGroup({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-white/55">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-white/10 bg-[#12161b] px-3 py-2.5 text-sm text-white outline-none transition focus:border-emerald-300/35"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} className="bg-[#12161b] text-white">
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function InsightLine({ icon: Icon, text }) {
  return (
    <div className="flex gap-3 rounded-2xl border border-white/8 bg-white/[0.025] p-3">
      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/7">
        <Icon className="h-3.5 w-3.5 text-white/65" />
      </div>
      <p className="text-sm leading-6 text-white/68">{text}</p>
    </div>
  );
}
